import {
  BlueBirdTheme,
  Button,
  Typography,
} from "@kitbird/core";
import { useQuery } from "react-query";
import { useEffect } from "react";
import { CardContent } from "../../../../components/Content/Content.style";
import {
  FormGroup,
  FormSection,
} from "../../../../components/Form/Form.style";
import useCreateReceiptSubmission from "../hooks/useCreateReceiptSubmission";
import {
  PageType,
} from "../enums";
import MultipleUpload from "./MultipleUpload/MultipleUpload";
import { getTrxType } from "../../../../services/trxTypeService/trxTypeService";
import { getBusinessCompany, getBusinessCompanyRelation } from "../../../../services/businessService/businessService";
import { getGLAccounts, getGLAccountsWithRelation } from "../../../../services/trxCodeSetService";
import { getVendorAccounts } from "../../../../services/vendorSetService";
import DetailSubmissionForm from "./DetailSubmissionForm";
import DetailApplicantForm from "./DetailApplicantForm";
import useAuth from "../../../../components/Authentication/useAuth";

interface SubmissionFormI {
  setPageType: React.Dispatch<React.SetStateAction<PageType>>,
  useCustomHook: ReturnType<typeof useCreateReceiptSubmission>,
}

const { gray } = BlueBirdTheme.colors;

const SubmissionForm = ({ setPageType, useCustomHook }: SubmissionFormI) => {
  const {
    onFileBrowse,
    doDeleteFileIndex,
    uploadedMedia,
    setGlAccount,
    setDestinationAccount,
    onSubmit,
    methods,
    refetchTable,
    setTrxTypeOpts,
    setDepartmentOpts,
    setSegmentOpts,
    setCompanyOpts,
    setCompanyRelationOpts,
    setGLAccountOpts,
    setVendorAccountOpts,
  } = useCustomHook;
  const { userAccount } = useAuth();
  const {
    getValues, reset, setValue, watch,
  } = methods;

  const { data: dataTrxType } = useQuery({
    queryKey: "get_trx_type",
    queryFn: () => getTrxType(),
  });

  useEffect(() => {
    if (dataTrxType) {
      setTrxTypeOpts(dataTrxType.lstmsttrxtype.map((type) => (
        { label: type.description, value: type.trx_type }
      )));
    }
  }, [dataTrxType]);

  useEffect(() => {
    const listDepartments = Array.from(
      new Set(userAccount.position_info.map((item) => item.department)),
    );
    const userDepartmentOpts = listDepartments.map((department) => ({
      label: department,
      value: department,
    }));
    setDepartmentOpts(userDepartmentOpts);
    if (userDepartmentOpts.length === 1) setValue("department", userDepartmentOpts[0].label);

    const codeSegments = Array.from(
      new Set(userAccount.position_info.map((item) => JSON.stringify({
        label: item.segments_info.desc_segment,
        value: item.segments_info.code_segment,
      }))),
    );
    const userSegmentOpts = codeSegments.map((item) => JSON.parse(item));
    setSegmentOpts(userSegmentOpts);
    if (userSegmentOpts.length === 1) setValue("segment", userSegmentOpts[0]);
  }, [userAccount]);

  useEffect(() => {
    if (
      getValues("codeType.value")
      && getValues("area.value")
      && getValues("pool.value")
      && getValues("segment.value")
      && !getValues("company")
    ) {
      getBusinessCompany(
        getValues("codeType").value as string,
        getValues("area").value as string,
        getValues("pool").value as string,
        getValues("segment").value as string,
      ).then((val) => {
        const newCompanyOpts = val.lstmstbusiness.map((company) => ({
          label: company?.company_code || "",
          value: company?.company_code || "",
        }));
        setCompanyOpts(newCompanyOpts);
        if (newCompanyOpts.length === 1) setValue("company", newCompanyOpts[0]);
        else setValue("company", { label: "", value: "" });
      });
    } else if (
      getValues("codeType.value")
      && getValues("area.value")
      && getValues("pool.value")
      && getValues("segment.value")
      && getValues("company.value")
      && getValues("company").value === "KBBO"
    ) {
      getBusinessCompanyRelation(
        getValues("codeType").value as string,
        getValues("area").value as string,
        getValues("pool").value as string,
        getValues("segment").value as string,
        getValues("company").value as string,
      ).then((val) => {
        const newCompanyRelationOpts = val.lstmstbusiness.map((company) => ({
          label: company?.company_relation || "",
          value: company?.company_relation || "",
        }));
        setCompanyRelationOpts(newCompanyRelationOpts);
        if (newCompanyRelationOpts.length === 1) setValue("companyRelation", newCompanyRelationOpts[0]);
        else setValue("companyRelation", { label: "", value: "" });
      });
    }
  }, [watch("codeType"), watch("area"), watch("pool"), watch("segment"), watch("company")]);

  useEffect(() => {
    if (
      getValues("trxType.value")
      && getValues("codeType.value")
      && getValues("area.value")
      && getValues("pool.value")
      && getValues("department")
      && getValues("company.value")
      && getValues("segment.value")
      && !getValues("companyRelation")
    ) {
      getGLAccounts(
        getValues("trxType").value as string,
        getValues("codeType").value as string,
        getValues("area").value as string,
        getValues("pool").value as string,
        getValues("department") as string,
        getValues("company").value as string,
        getValues("segment").value as string,
      ).then((val) => {
        setGLAccountOpts(val.lstsmplmsttrxcodeset);
        setGlAccount(undefined);
      });
    } else if (
      getValues("trxType.value")
      && getValues("codeType.value")
      && getValues("area.value")
      && getValues("pool.value")
      && getValues("department")
      && getValues("company.value")
      && getValues("segment.value")
      && getValues("companyRelation.value")
    ) {
      getGLAccountsWithRelation(
        getValues("trxType").value as string,
        getValues("codeType").value as string,
        getValues("area").value as string,
        getValues("pool").value as string,
        getValues("department") as string,
        getValues("company").value as string,
        getValues("segment").value as string,
        getValues("companyRelation").value as string,
      ).then((val) => {
        setGLAccountOpts(val.lstsmplmsttrxcodeset);
        setGlAccount(undefined);
      });
    }
    if (
      getValues("codeType.value")
      && getValues("area.value")
      && getValues("pool.value")
      && getValues("department")
      && getValues("company.value")
    ) {
      getVendorAccounts(
        getValues("codeType").value as string,
        getValues("area").value as string,
        getValues("pool").value as string,
        getValues("department") as string,
        getValues("company")?.value as string,
      ).then((val) => {
        setVendorAccountOpts(val.lstsmplmstvendorset);
        setDestinationAccount(undefined);
      });
    }
  }, [watch("company"), watch("trxType"), watch("codeType"), watch("area"), watch("pool"), watch("department"), getValues("segment")]);

  const onSubmitSuccess = () => {
    setPageType(PageType.LIST);
    reset(undefined);
    refetchTable();
  };

  return (
    <CardContent>
      <FormSection style={{ marginRight: "-24px", paddingRight: 24 }}>
        <DetailApplicantForm useCustomHook={useCustomHook} />
        <DetailSubmissionForm useCustomHook={useCustomHook} />
        <Typography level={6} color={gray.title} fontWeight="medium">
          Lampiran
        </Typography>
        <MultipleUpload
          onFileBrowse={onFileBrowse}
          doDeleteFileIndex={doDeleteFileIndex}
          listFile={uploadedMedia}
          accept=".pdf, .jpeg, .jpg, .png"
        />
      </FormSection>
      <FormGroup $justify="flex-end">
        <Button onClick={() => {
          refetchTable();
          setPageType(PageType.LIST);
        }} variant="tertiary">
          Kembali
        </Button>
        <Button onClick={() => onSubmit(onSubmitSuccess)}>Simpan</Button>
      </FormGroup>
    </CardContent >
  );
};

export default SubmissionForm;
